import {
  ExceptionStatus,
  ScoreRating,
  ScoreResultType,
  ScoreState,
} from "~/operations";
import { CheckboxCell, ImpactCell, RiskFactorsCell } from "~/components/Cells";
import { Box, CheckboxProps, TableCell, TableRow } from "@mui/material";
import { CheckResultByText } from "~/components/impact/ByText/ByText";
import { TitleCell } from "~/pages/inventory/components/Findings/FindingsTable/TitleCell";
import React from "react";
import { CVEFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/AssetSoftwareVulnerabilities/hooks/types";
import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";
import {
  CheckFindingsCheckNode,
  GenericFindingsNode,
} from "~/pages/inventory/asset/types";
import { SwitchBaseProps } from "@mui/material/internal/SwitchBase";
import { FindingContext } from "~/pages/inventory/components/Findings/FindingsTable/utils/findingTableUtils";
import { EXCEPTIONS_SCORE_STATES } from "~/pages/findings/Exceptions/constants";
import { ActionCell } from "~/components/FirewatchRow/ActionCell";

interface FindingsTableRowProps {
  finding:
    | CVEFindingsNode
    | AdvisoryFindingsNode
    | CheckFindingsCheckNode
    | GenericFindingsNode;
  context: FindingContext;
  canSelect: boolean;
  onClick?: React.MouseEventHandler<HTMLTableRowElement> | undefined;
  isSelected?: boolean;
  onCheckboxSelect?: SwitchBaseProps["onChange"];
  onCheckboxClick: CheckboxProps["onClick"];
  onPendingExceptionClick?: (() => void) | undefined;
  showAction?: boolean;
}

export const FindingsTableRow = ({
  finding,
  context,
  canSelect = false,
  onClick,
  isSelected = false,
  onCheckboxSelect,
  onCheckboxClick,
  onPendingExceptionClick,
  showAction = true,
}: FindingsTableRowProps) => {
  const isCheckFinding = finding.__typename === "CheckFinding";
  const isCveFinding = finding.__typename === "CveFinding";
  const isAdvisoryFinding = finding.__typename === "AdvisoryFinding";
  const isGenericFinding = finding.__typename === "GenericFinding";

  const isCriticalExposure = context === "critical_exposure";
  const isAll = context === "all";

  const getIconId = () => {
    if (isCriticalExposure && isCveFinding) {
      return finding?.asset?.icon;
    }
    if (isAdvisoryFinding) {
      return finding.iconId;
    }
    return undefined;
  };

  const getTags = () => {
    const findingTags = finding.tags || [];
    if (isCriticalExposure && isCveFinding) {
      const assetTags = finding.asset?.tags || [];
      const tags = [...findingTags, ...assetTags].filter((tag) => tag !== null);
      return tags;
    }
    return findingTags;
  };

  return (
    <TableRow
      id={finding.id}
      key={finding.id}
      onClick={onClick}
      sx={{
        cursor: "pointer",
        "&.MuiTableRow-root:hover .firewatch-chip": {
          backgroundColor: "background.lighter",
        },
      }}
    >
      {canSelect && (
        <CheckboxCell
          onClick={onCheckboxClick}
          onChange={onCheckboxSelect}
          checked={isSelected}
        />
      )}
      {isCheckFinding ? (
        <TableCell className="risk-cell" width="100px">
          <Box sx={{ display: "flex", gap: 1 }}>
            <CheckResultByText
              scoreResult={finding.resultType || ScoreResultType.Unknown}
              scoreState={finding.state || ScoreState.Open}
              rating={finding.rating || ScoreRating.Critical}
            />
          </Box>
        </TableCell>
      ) : (
        <ImpactCell
          data-testid={`${context}-impact`}
          rating={finding.rating}
          impact={finding.riskValue || 0}
          isActive={finding.state === ScoreState.Open}
          cellProps={{ width: "10%" }}
        />
      )}
      <TitleCell
        data-testid={`${context}-title`}
        id={!(isCheckFinding || isGenericFinding) ? finding.id : ""}
        title={finding.title}
        {...(isCriticalExposure &&
          isCveFinding && {
            asset: finding.asset,
          })}
        tags={getTags()}
        state={finding.state}
        iconId={getIconId()}
        isPendingException={
          !isGenericFinding &&
          finding?.exception?.status === ExceptionStatus.New
        }
        exception={!isGenericFinding ? finding.exception : undefined}
        onPendingExceptionClick={onPendingExceptionClick}
      />
      {isGenericFinding ? (
        <TableCell />
      ) : (
        <RiskFactorsCell
          data-testid={`${context}-risk-factors`}
          riskFactors={finding.riskFactors}
          isActive={!EXCEPTIONS_SCORE_STATES.includes(finding.state)}
        />
      )}
      {isGenericFinding ? (
        <TableCell />
      ) : (
        ((isCveFinding && isCriticalExposure) || isAll) &&
        showAction && <ActionCell row={finding} />
      )}
    </TableRow>
  );
};
